'use client';
import { useTimePilotT } from "@/i18n/useTimePilotT";

import { useEffect, useRef } from 'react';
import { mobileUserFacingErrorMessage } from '@/shared/mobile-service/api/cors-mobile-api';
import { isFocusedMessage } from '@/shared/mobile-service/lib/citation-focus';
import type { MappedMessage } from '@/shared/mobile-service/lib/message-mapping';
import { EmptyState, ErrorState, LoadingState } from '../ui';
import { MessageBubble } from './MessageBubble';

/**
 * Right pane: the selected thread's messages as chat bubbles (oldest → newest).
 */
export function MessageBubbles({
  messages,
  threadSelected,
  isLoading,
  error,
  focusIds = [],
  highlightIds = [],
  onRetry,
  onSelectMessage,
}: {
  messages: MappedMessage[];
  threadSelected: boolean;
  isLoading: boolean;
  error: unknown;
  focusIds?: Array<string | number>;
  highlightIds?: Array<string | number>;
  onRetry: () => void;
  onSelectMessage: (message: MappedMessage) => void;
}) {
  const t = useTimePilotT();
  const focusRef = useRef<HTMLDivElement | null>(null);
  const hasFocus = focusIds.length > 0;
  const firstFocusedId = hasFocus
    ? messages.find((message) => isFocusedMessage(message, focusIds))?.id
    : undefined;

  useEffect(() => {
    if (!firstFocusedId || !focusRef.current) return;
    focusRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [firstFocusedId]);

  if (!threadSelected) {
    return <EmptyState message={t("viewers.selectAConversationToViewItsMessages")} />;
  }
  if (isLoading) return <LoadingState label={t("viewers.loadingMessages")} />;
  if (error) return <ErrorState message={mobileUserFacingErrorMessage(error)} onRetry={onRetry} />;
  if (messages.length === 0) {
    return <EmptyState message={t("viewers.thisConversationHasNoMessages")} />;
  }

  return (
    <div className="flex flex-col gap-2 px-4 py-4">
      {messages.map((message) => {
        const cited = isFocusedMessage(message, focusIds);
        const highlighted = isFocusedMessage(message, highlightIds);
        return (
          <div key={message.id} ref={cited && message.id === firstFocusedId ? focusRef : undefined}>
            <MessageBubble
              message={message}
              focused={highlighted}
              dimmed={hasFocus && !cited}
              onSelect={onSelectMessage}
            />
          </div>
        );
      })}
    </div>
  );
}
